// Which sheet is open, and every write the create-and-edit flow makes.
//
// The day screen reads; this writes. They are separate hooks because they fail
// differently: a read that fails leaves a screen with no day on it, while a
// write that fails leaves a sheet still open with the words the person typed
// still in it - so a failure here belongs INSIDE the sheet, next to the button
// that did not work, and never in the toast behind its own scrim.
//
// Two rules run through all of it:
//
//   ARCHIVE IS THE DESTRUCTIVE DEFAULT, because archiving is the one that can
//   be undone. `DELETE` archives and `?permanent=true` deletes forever
//   (AGENTS.md), so every button here that destroys something offers Undo, and
//   the single one that cannot - deleting forever - asks first instead of
//   promising a way back it does not have.
//
//   A SCHEDULE IS NEVER DELETED WITHOUT ITS ITEMS. `section_id` is
//   `on delete set null` and a routine with no schedule IS a reminder, so a
//   schedule deleted the other way turns its items into loose reminders.
//   `deleteSectionForever()` does both in one transaction, and `?permanent=true`
//   is the only thing that reaches it - which is why nothing in this file ever
//   clears an item's `sectionId` or deletes routines one by one to empty a
//   schedule.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isSignedOut, messageFor, requireAppliedRoutineNoteWrite } from '../api/client.ts';
import type { DayResponse, IsoDay, RecurrenceSpec, RoutinePatch } from '../api/client.ts';
import type { EditableSchedule } from './edit-model.ts';
import type { NewReminderInput } from '../reminders/reminders-model.ts';
import { dayAndMonth } from '../iso-day.ts';

/** Which sheet is on screen. Null is the day screen with nothing over it. */
export type EditSheet =
  /** "Add something": the two kinds of thing this client can make. */
  | { kind: 'add' }
  /** The three-step new-schedule flow, or the cap notice in its place. */
  | { kind: 'new-schedule' }
  /** A new item. `sectionId` is the schedule it starts in, when one is known. */
  | { kind: 'new-item'; sectionId: string | null }
  /**
   * A new reminder - a thing that belongs to no schedule.
   *
   * `from` is which surface asked, and it decides one sentence and nothing
   * else: a reminder is written down TODAY, so one made while Tomorrow is on
   * the day screen lands on a day that screen is not showing and has to say
   * so, while one made on the reminders list is visibly there the moment it is
   * saved and needs no explanation. The alternative - asking the day payload
   * every time - answers "it is not on 13 August" on a screen that is not
   * about 13 August at all.
   */
  | { kind: 'new-reminder'; from: 'day' | 'reminders' }
  /** Editing the one routine row behind a reminder. */
  | { kind: 'reminder'; reminderId: string }
  /**
   * Editing a schedule: name, colour, icon, archive, delete forever.
   *
   * Carried as an `EditableSchedule` rather than as the row either place holds,
   * because there are two ways in - a card on the day, and the account's list
   * in Settings - and only one of them knows what is due in it today.
   */
  | { kind: 'schedule'; schedule: EditableSchedule }
  /**
   * Editing an item. The row is fetched by id, because the day payload has the
   * recurrence in words and not the rule; `meta` is those words, carried from
   * the row that was tapped so nothing has to phrase a rule twice.
   */
  | { kind: 'item'; itemId: string; meta: string };

export interface NewScheduleInput {
  name: string;
  colorHex: string;
  symbol: string;
  first: { title: string; rule: RecurrenceSpec | null; evening: boolean };
}

export interface NewItemInput {
  sectionId: string;
  title: string;
  note: string;
  rule: RecurrenceSpec | null;
  evening: boolean;
}

/** What this hook needs from the day screen it sits over. */
export interface EditorHost {
  /** Said after anything was written, so the day catches up. */
  refresh: () => void;
  /** Re-reads the day/reminders and invalidates the Notes list together. */
  onRoutineNoteChanged: () => void;
  say: (message: string, undo?: () => void, retire?: () => void) => void;
  /** Takes the toast away, which is how an Undo stops being pressable twice. */
  dismiss: () => void;
  signOut: () => void;
  /**
   * The day on screen, which is the day a new thing is anchored to.
   *
   * Anchoring to the day being LOOKED AT rather than to the server's today is
   * what makes "add this to tomorrow" mean what it says: a one-off is due on
   * its anchor day and no other, and a repeat starts there. Null while no day
   * is known, which is also when nothing can be added.
   */
  anchorDay: IsoDay | null;
  /**
   * Keeps a reminder just created in the day block's visible set, on the day it
   * lands on. The block draws two rows and nothing here sorts them, so without
   * it a new reminder is usually below the fold of its own card.
   */
  pinReminder: (routineId: string) => void;
}

export interface EditorState {
  sheet: EditSheet | null;
  /** A write is in flight; the sheet's buttons wait for it. */
  busy: boolean;
  /** Why the last write in this sheet did not happen. Cleared by the next try. */
  problem: string | null;
  routineNoteConflict: boolean;
  open: (sheet: EditSheet) => void;
  close: () => void;
  createSchedule: (input: NewScheduleInput) => Promise<void>;
  saveSchedule: (
    id: string,
    patch: { name: string; colorHex: string; symbol: string },
  ) => Promise<void>;
  archiveSchedule: (id: string, name: string) => Promise<void>;
  /** The other half of archiving, from the schedules list in Settings. */
  restoreSchedule: (id: string, name: string) => Promise<void>;
  deleteScheduleForever: (id: string, name: string) => Promise<void>;
  createItem: (input: NewItemInput) => Promise<void>;
  saveItem: (id: string, patch: RoutinePatch) => Promise<void>;
  archiveItem: (id: string, title: string) => Promise<void>;
  deleteItemForever: (id: string, title: string) => Promise<void>;
  createReminder: (input: NewReminderInput, from: 'day' | 'reminders') => Promise<void>;
  saveReminder: (id: string, patch: RoutinePatch) => Promise<void>;
  /** Takes a reminder off the list, with an Undo. The reminders screen's. */
  removeReminder: (id: string, title: string) => Promise<void>;
}

type Attempt<T> = { ok: true; value: T } | { ok: false; signedOut: boolean };

export function useEditor(host: EditorHost): EditorState {
  const [sheet, setSheet] = useState<EditSheet | null>(null);
  const [busy, setBusy] = useState(false);
  const writesInFlight = useRef(0);
  const [problem, setProblem] = useState<string | null>(null);
  const [routineNoteConflict, setRoutineNoteConflict] = useState(false);

  const { refresh, onRoutineNoteChanged, say, dismiss, signOut, anchorDay, pinReminder } = host;

  /**
   * The control focus came from when the FIRST sheet of a run opened, so it can
   * go back there when the last one closes (WAI-ARIA APG). It belongs here
   * rather than to a panel because several panels are one act to the person in
   * front of them: + opens the chooser, the chooser opens the wizard, the
   * wizard's three steps replace one another, and a sheet that reads first
   * swaps its "Loading…" panel for its form.
   * Each of those unmounts a panel whose "opener" is a button inside the panel
   * before it - which is gone moments later - so a panel restoring to its own
   * predecessor lands on a detached node and drops focus onto <body>, which is
   * the whole defect.
   */
  const opener = useRef<HTMLElement | null>(null);

  /**
   * Taken in the EVENT that opens the sheet, which is the last moment it is
   * still true. Two things move focus in the commit that follows, and both are
   * this client's own doing:
   *
   *   `autoFocus` on a form's first field is applied in React's layout phase,
   *   so anything reading `document.activeElement` from an effect afterwards
   *   sees that field rather than the button that was pressed.
   *
   *   `inert` goes onto the whole screen and the whole navigation in that same
   *   commit, and the HTML focus-fixup rule blurs an element that becomes inert
   *   while it holds focus - so by then the row or card that was pressed has
   *   already been reset to <body>.
   */
  const open = useCallback((next: EditSheet) => {
    if (opener.current === null) {
      const active = document.activeElement;
      opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
    }
    setProblem(null);
    setRoutineNoteConflict(false);
    setSheet(next);
  }, []);

  const close = useCallback(() => {
    if (writesInFlight.current > 0) return;
    setProblem(null);
    setRoutineNoteConflict(false);
    setSheet(null);
  }, []);

  /**
   * And given back once the last sheet is gone - after the commit rather than
   * during `close()`, because focus cannot be put back on a control whose
   * ancestor is still `inert`: the browser refuses it and the caller cannot
   * tell. By the time this runs the sheet is unmounted and the pane is live
   * again, so the same call lands.
   *
   * Two guards, both ordinary here. The opener may be GONE - archiving an item
   * unmounts the very row whose button opened the sheet - and focus may have
   * MOVED ON to something else, which is not this hook's to take away.
   */
  useEffect(() => {
    if (sheet !== null) return;
    const back = opener.current;
    opener.current = null;
    const focused = document.activeElement;
    if (focused !== null && focused !== document.body) return;
    if (back !== null && back.isConnected) back.focus();
  }, [sheet]);

  /**
   * One write, with its failure put where the person is looking.
   *
   * A 401 is the exception, as it is everywhere else in this client: it is not
   * a failure of the request but of the session behind it, so it raises the
   * signed-out screen rather than writing a sentence into a sheet that is about
   * to be replaced along with the rest of the shell.
   */
  const attempt = useCallback(
    async <T>(work: () => Promise<T>): Promise<Attempt<T>> => {
      writesInFlight.current += 1;
      setBusy(true);
      setProblem(null);
      try {
        return { ok: true, value: await work() };
      } catch (error) {
        if (isSignedOut(error)) {
          signOut();
          return { ok: false, signedOut: true };
        }
        setProblem(messageFor(error));
        return { ok: false, signedOut: false };
      } finally {
        writesInFlight.current -= 1;
        if (writesInFlight.current === 0) setBusy(false);
      }
    },
    [signOut],
  );

  /**
   * The Undo on a toast. It runs with no sheet on screen, so its own failure
   * has nowhere to go but the toast - and it has to be said, because an undo
   * that quietly does nothing is worse than no undo at all: the person walks
   * away believing the thing is back.
   *
   * The line goes FIRST, before the request it sends. The day behind it takes a
   * read to catch up, so a button left on screen in the meantime is one that
   * can be pressed again - and the second press arrives at a schedule already
   * restored, or an id already deleted, and is reported as "That could not be
   * undone" for the undo that in fact worked. Sending somebody looking for
   * damage that is not there is worse than the message it replaced.
   */
  const undoWith = useCallback(
    (work: () => Promise<unknown>, after: () => void = refresh) =>
      () => {
        dismiss();
        void (async () => {
          try {
            await work();
            after();
          } catch (error) {
            if (isSignedOut(error)) {
              signOut();
              return;
            }
            say(`That could not be undone. ${messageFor(error)}`);
          }
        })();
      },
    [dismiss, refresh, say, signOut],
  );

  /**
   * The day the new thing was anchored to, when that day does not show it - and
   * null when it does, or when the answer cannot be had.
   *
   * A repeat that misses the day on screen is written successfully and then
   * appears nowhere, which is indistinguishable from a failure: the natural
   * next move is to add it again, and now there are two. So the toast says it
   * plainly instead.
   *
   * Whether a thing is due on a day is the SERVER'S answer - occurrences are
   * never stored, they are computed from the rule at read time (AGENTS.md) - so
   * this asks the day rather than working it out from the rule it just sent. A
   * second copy of that arithmetic here is exactly what the payload's
   * server-computed words exist to prevent. It names no other day: which day it
   * IS next due is not in anything the server sends today, and a helpful-looking
   * date that could be wrong is worse than an honest "not this one".
   */
  const omittedOn = useCallback(
    async (drawn: (day: DayResponse) => boolean): Promise<IsoDay | null> => {
      if (anchorDay === null) return null;
      try {
        const day = await api.day(anchorDay);
        return drawn(day) ? null : anchorDay;
      } catch {
        // The write landed; only the check did not. The plain line is still
        // true, and the read the day screen is doing anyway reports its own
        // failure - this one must not invent a second.
        return null;
      }
    },
    [anchorDay],
  );

  /** The day, when it does not draw this item. */
  const notShownOn = useCallback(
    (routineId: string): Promise<IsoDay | null> =>
      omittedOn((day) => day.sections.some((s) => s.items.some((item) => item.id === routineId))),
    [omittedOn],
  );

  /**
   * The day, when it does not draw this schedule - which is the same question
   * asked of a whole card rather than a row. A schedule is drawn by what is due
   * IN it, so one with nothing due today has no card at all; asking about the
   * section id is the only way to tell that from "it is not there".
   */
  const scheduleNotShownOn = useCallback(
    (sectionId: string): Promise<IsoDay | null> =>
      omittedOn((day) => day.sections.some((s) => s.id === sectionId)),
    [omittedOn],
  );

  const createSchedule = useCallback(
    async (input: NewScheduleInput) => {
      const made = await attempt(() =>
        api.createSection({ name: input.name, colorHex: input.colorHex, symbol: input.symbol }),
      );
      if (!made.ok) return;
      const section = made.value;

      // A schedule and its first thing are one act to the person who pressed
      // Create, so a half-finished one is not left behind: an empty schedule
      // shows on no day at all - a day leaves out the schedules with nothing
      // due - while still counting against the cap, which is the worst of both.
      const first = await attempt(() =>
        api.createRoutine({
          sectionId: section.id,
          title: input.first.title,
          rule: input.first.rule,
          evening: input.first.evening,
          ...(anchorDay === null ? {} : { anchorDay }),
        }),
      );

      if (!first.ok) {
        if (first.signedOut) return;
        try {
          await api.deleteSectionForever(section.id);
        } catch {
          // The rollback is the second thing to fail, so the sheet is not the
          // place for it: close, tell the truth about what is now on the
          // account, and let the day show it.
          close();
          refresh();
          say(`"${section.name}" was made, but the first thing in it was not saved.`);
        }
        return;
      }

      close();
      refresh();
      // A schedule is drawn by what is due in it, so a schedule whose only
      // thing misses the day on screen is a schedule that appears nowhere.
      const missing = await notShownOn(first.value.id);
      // Undone by deleting rather than archiving: it was made seconds ago and
      // has no history to keep, and an archived ghost is not what "undo" means.
      // Deleting takes its first item with it, in the one transaction.
      say(
        missing === null
          ? `"${section.name}" added.`
          : `"${section.name}" added. Nothing in it is due on ${dayAndMonth(missing)}.`,
        undoWith(() => api.deleteSectionForever(section.id)),
      );
    },
    [attempt, anchorDay, close, notShownOn, refresh, say, undoWith],
  );

  const saveSchedule = useCallback(
    async (id: string, patch: { name: string; colorHex: string; symbol: string }) => {
      const saved = await attempt(() => api.updateSection(id, patch));
      if (!saved.ok) return;
      close();
      refresh();
      // Named, as the archive and the delete on this same path already are,
      // because the result can be invisible: the schedules list in Settings
      // opens schedules the day does not draw, and a rename there leaves the
      // day byte-identical.
      // Naming what was written IS the confirmation.
      //
      // And named is ALL it says. There is deliberately no "it is not due on
      // …" second sentence here and no second question asked of the re-read
      // day: that sentence exists to explain a DISAPPEARANCE, and nothing
      // disappears from a rename - a dormant schedule's card was not on screen
      // before the save either. Applying the create-and-edit rule mechanically
      // here would answer a question nobody asked.
      say(`"${saved.value.name}" saved.`);
    },
    [attempt, close, refresh, say],
  );

  const archiveSchedule = useCallback(
    async (id: string, name: string) => {
      const done = await attempt(() => api.archiveSection(id));
      if (!done.ok) return;
      close();
      refresh();
      say(
        `"${name}" archived. Everything you finished in it is kept.`,
        // Restoring can hit the cap - the trigger counts active schedules - and
        // that refusal reaches the person through the toast, which is why the
        // undo path reports its own failures rather than assuming it worked.
        undoWith(() => api.updateSection(id, { archived: false })),
      );
    },
    [attempt, close, refresh, say, undoWith],
  );

  /**
   * Putting an archived schedule back, from the list in Settings.
   *
   * It does NOT go through `attempt`, and that is the point rather than an
   * oversight: `attempt` puts a failure in `problem`, which is drawn inside a
   * sheet, and this button is on a screen with no sheet over it - so the reason
   * would be written somewhere nobody is looking. The toast is where a
   * screen-level failure belongs, and it is already the only thing on the page
   * that can carry one.
   *
   * The failure this is really for is the CAP. The trigger counts active
   * schedules, so restoring the eleventh is refused by the database, and the
   * sentence the person needs is the trigger's own - which arrives in the 409
   * and is repeated verbatim rather than restated here (AGENTS.md: no client
   * ever writes the cap down).
   */
  const restoreSchedule = useCallback(
    async (id: string, name: string) => {
      let restored: string;
      try {
        restored = (await api.updateSection(id, { archived: false })).name;
      } catch (error) {
        if (isSignedOut(error)) {
          signOut();
          return;
        }
        say(`"${name}" was not restored. ${messageFor(error)}`);
        return;
      }

      refresh();
      // A restored schedule is drawn by what is due in it, exactly as a new one
      // is - so putting a weekend schedule back on a Tuesday changes nothing
      // visible, and saying so is the same rule that covers a create that lands
      // on no day (web/src/README.md).
      const missing = await scheduleNotShownOn(id);
      say(
        missing === null
          ? `"${restored}" restored.`
          : `"${restored}" restored. Nothing in it is due on ${dayAndMonth(missing)}.`,
        undoWith(() => api.archiveSection(id)),
      );
    },
    [refresh, say, scheduleNotShownOn, signOut, undoWith],
  );

  const deleteScheduleForever = useCallback(
    async (id: string, name: string) => {
      const done = await attempt(() => api.deleteSectionForever(id));
      if (!done.ok) return;
      close();
      refresh();
      // No undo, and none offered: this is the one action the API cannot take
      // back, which is why the button asks first. Saying so is the honest
      // version of an Undo that would do nothing.
      say(`"${name}" deleted, with its items.`);
    },
    [attempt, close, refresh, say],
  );

  const createItem = useCallback(
    async (input: NewItemInput) => {
      const made = await attempt(() =>
        api.createRoutine({
          sectionId: input.sectionId,
          title: input.title,
          note: input.note,
          rule: input.rule,
          evening: input.evening,
          ...(anchorDay === null ? {} : { anchorDay }),
        }),
      );
      if (!made.ok) return;
      close();
      refresh();
      const missing = await notShownOn(made.value.id);
      say(
        missing === null
          ? `"${made.value.title}" added.`
          : `"${made.value.title}" added. It is not due on ${dayAndMonth(missing)}.`,
        undoWith(() => api.deleteRoutineForever(made.value.id)),
      );
    },
    [attempt, anchorDay, close, notShownOn, refresh, say, undoWith],
  );

  const saveItem = useCallback(
    async (id: string, patch: RoutinePatch) => {
      const saved = await attempt(() => api.updateRoutine(id, patch));
      if (!saved.ok) return;
      if (saved.value.noteWriteApplied === false) {
        setRoutineNoteConflict(true);
        setProblem(
          `"${patch.title ?? saved.value.title}" was not saved. That note changed after this edit began. Close and reopen it before saving again.`,
        );
        return;
      }
      close();
      refresh();
      // Named on BOTH branches, because every successful write in this file
      // names what it acted on and this one is not an exception to that
      // (web/src/README.md). The second sentence is the exception, and only
      // where it is earned: an edit that moves a repeat off the day being
      // looked at takes the row off the screen, and a line over a row that has
      // just vanished is indistinguishable from a failure.
      //
      // That is the WORSE of the two cases the sentence exists for. A created
      // thing that lands on no day is at least somewhere to be found later; a
      // dormant item has no entry point at all - Settings lists schedules and
      // nothing lists items - so without it the row is simply gone, the app
      // said it saved, and there is nowhere to go and see what happened.
      const missing = await notShownOn(id);
      const saidSaved = `"${saved.value.title}" saved.`;
      say(missing === null ? saidSaved : `${saidSaved} It is not due on ${dayAndMonth(missing)}.`);
    },
    [attempt, close, notShownOn, refresh, say],
  );

  const archiveItem = useCallback(
    async (id: string, title: string) => {
      const done = await attempt(() => api.archiveRoutine(id));
      if (!done.ok) return;
      close();
      refresh();
      say(
        `"${title}" archived. The days you finished it are kept.`,
        undoWith(() => api.updateRoutine(id, { archived: false })),
      );
    },
    [attempt, close, refresh, say, undoWith],
  );

  const deleteItemForever = useCallback(
    async (id: string, title: string) => {
      const done = await attempt(() => api.deleteRoutineForever(id));
      if (!done.ok) return;
      close();
      refresh();
      say(`"${title}" deleted.`);
    },
    [attempt, close, refresh, say],
  );

  // ------------------------------------------------------- reminders -------
  //
  // A reminder is a routine with no schedule, so both writes below are the
  // ordinary routine writes with `sectionId: null` - no new endpoint, and the
  // same undo the rest of this file offers.

  /** The day, when its reminders do not include this one. */
  const reminderNotShownOn = useCallback(
    (routineId: string): Promise<IsoDay | null> =>
      omittedOn((day) => day.reminders.some((reminder) => reminder.id === routineId)),
    [omittedOn],
  );

  /**
   * A NEW REMINDER IS WRITTEN DOWN TODAY, and carries no `anchorDay` for it.
   *
   * Every other create in this file anchors to the day being LOOKED AT, because
   * "add this to tomorrow" has to mean tomorrow. A reminder is the one thing
   * that cannot: its anchor day is the day it was written down
   * (api/src/domain/day.ts), the day read shows it from that day through today
   * inclusive, and a completion may only be written in that window. Anchoring
   * one to tomorrow would therefore create a row that is on NO day at all -
   * not tomorrow, because a day that has not arrived is not a day anything was
   * open on, and not today, because it had not been written down yet. So the
   * field is omitted and the server's own today answers.
   *
   * Which leaves the sentence. A reminder made while Yesterday or Tomorrow is
   * on screen is correct and is not on that screen, and a successful write
   * whose result cannot be seen is what a failure looks like from the outside -
   * the trap this client has met five times (web/src/README.md). So the day is
   * asked, exactly as it is for an item that lands on no day, and the answer is
   * said. `from` keeps that question off the reminders list, where the new row
   * is visibly there and "it is not on 13 August" would be about nothing on
   * screen.
   *
   * AND THE PIN IS WHAT MAKES THAT SENTENCE'S SILENCE TRUE. `reminderNotShownOn`
   * asks whether the day payload CONTAINS the new id, which is not the same
   * question as whether the block DRAWS it - the block draws two rows, nothing
   * in this client sorts reminders, and an undated one sorts last. So without
   * the pin the check says nothing is wrong while the only thing that moved on
   * screen is a footer count. Do not remove it as redundant; that is the shape
   * of the bug it closed (prototype-v2.md section 1.2 pins a reminder created in
   * this sitting exactly as it pins one ticked in this sitting).
   */
  const createReminder = useCallback(
    async (input: NewReminderInput, from: 'day' | 'reminders') => {
      const made = await attempt(() =>
        api.createRoutine({
          sectionId: null,
          title: input.title,
          ...(input.dueDay === null ? {} : { dueDay: input.dueDay }),
          ...(input.note === undefined ? {} : { note: input.note }),
        }),
      );
      if (!made.ok) return;
      close();
      pinReminder(made.value.id);
      onRoutineNoteChanged();
      const missing = from === 'day' ? await reminderNotShownOn(made.value.id) : null;
      say(
        missing === null
          ? `"${made.value.title}" added.`
          : `"${made.value.title}" added to your reminders. It is not on ${dayAndMonth(missing)}.`,
        // Deleted rather than archived by the undo, as a new schedule is: it
        // was made seconds ago and has no history to keep, and an archived
        // ghost is not what "undo" means.
        undoWith(() => api.deleteRoutineForever(made.value.id), onRoutineNoteChanged),
      );
    },
    [attempt, close, onRoutineNoteChanged, pinReminder, reminderNotShownOn, say, undoWith],
  );

  /** Save the same routine fields the Notes tab writes, then re-read every surface. */
  const saveReminder = useCallback(
    async (id: string, patch: RoutinePatch) => {
      const outcome = await attempt(async () => {
        const updated = await api.updateRoutine(id, patch);
        const removedNote =
          patch.note !== undefined && updated.note === '' ? updated.previousNote : null;
        const operationId = updated.noteClearOperationId;
        let clearCompletion: 'recoverable' | 'unavailable' | 'superseded' = 'unavailable';
        if (removedNote !== null && removedNote !== '' && operationId !== null) {
          try {
            clearCompletion = await api.completeRoutineNoteClear(updated);
          } catch (error) {
            if (isSignedOut(error)) signOut();
          }
        }
        return { updated, removedNote, operationId, clearCompletion };
      });
      if (!outcome.ok) return;
      const { updated, removedNote, operationId, clearCompletion } = outcome.value;
      if (clearCompletion === 'superseded') return;
      const undoStarted = clearCompletion === 'recoverable';
      onRoutineNoteChanged();
      if (updated.noteWriteApplied === false) {
        setRoutineNoteConflict(true);
        setProblem(
          `"${patch.title ?? updated.title}" was not saved. That note changed after this edit began. Close and reopen it before saving again.`,
        );
        return;
      }
      close();
      if (removedNote === null || removedNote === '' || operationId === null) {
        say(`"${updated.title}" saved.`);
        return;
      }
      if (!undoStarted) {
        say(`The note on "${updated.title}" was removed.`);
        return;
      }
      let restoring = false;
      const restore = undoWith(
        async () =>
          requireAppliedRoutineNoteWrite(
            await api.updateRoutine(id, {
              note: removedNote,
              noteRestoreOperationId: operationId,
            }),
          ),
        onRoutineNoteChanged,
      );
      say(
        `The note on "${updated.title}" was removed.`,
        () => {
          restoring = true;
          restore();
        },
        () => {
          if (!restoring) {
            void api.retireRoutineNoteClear(id, operationId).catch(() => undefined);
          }
        },
      );
    },
    [attempt, close, onRoutineNoteChanged, say, signOut, undoWith],
  );

  /**
   * Taking a reminder off the list - the way out of one written by mistake.
   *
   * ARCHIVING, not deleting, so it is the destructive action that can be undone
   * (AGENTS.md), and the Undo is the whole reason this exists at all: without
   * it the only way to be rid of a reminder is to tick it, which writes a
   * completion the year retrospective will read as work that was done. A false
   * row in an append-only log is not a tidiness problem.
   *
   * It does NOT go through `attempt`, for the same reason `restoreSchedule`
   * does not: `attempt` puts a failure in `problem`, which is drawn inside a
   * sheet, and this button is on a screen with no sheet over it - so the reason
   * would be written somewhere nobody is looking.
   */
  const removeReminder = useCallback(
    async (id: string, title: string) => {
      try {
        await api.archiveRoutine(id);
      } catch (error) {
        if (isSignedOut(error)) {
          signOut();
          return;
        }
        say(`"${title}" was not removed. ${messageFor(error)}`);
        return;
      }

      onRoutineNoteChanged();
      say(
        `"${title}" removed.`,
        undoWith(() => api.updateRoutine(id, { archived: false }), onRoutineNoteChanged),
      );
    },
    [onRoutineNoteChanged, say, signOut, undoWith],
  );

  return {
    sheet,
    busy,
    problem,
    routineNoteConflict,
    open,
    close,
    createSchedule,
    saveSchedule,
    archiveSchedule,
    restoreSchedule,
    deleteScheduleForever,
    createItem,
    saveItem,
    archiveItem,
    deleteItemForever,
    createReminder,
    saveReminder,
    removeReminder,
  };
}
